import React from "react";
import { useLocale } from "../../i18n";
import LegalShell from "./LegalShell";

const P = ({ children }) => <p className="text-[var(--wr-ink)]">{children}</p>;

export default function CookiePolicy() {
  const { locale } = useLocale();
  const en = locale === "en";

  const sections = en ? [
    {
      heading: "The short version",
      body: <>
        <P>WowRomania does not use advertising cookies. We do not use cross-site tracking. Everything below explains the small number of technical cookies and browser storage items we do use.</P>
        <P>We believe you should know exactly what your browser stores — and why.</P>
      </>,
    },
    {
      heading: "localStorage (in your browser only)",
      body: <>
        <P>Two small preferences are stored in your browser's localStorage. They never leave your device and we never see them:</P>
        <table className="w-full text-[14px] mt-2 border border-[var(--wr-border)]">
          <thead className="bg-[var(--wr-surface-2)]">
            <tr>
              <th className="text-left font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)] px-4 py-3 border-b border-[var(--wr-border)]">Key</th>
              <th className="text-left font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)] px-4 py-3 border-b border-[var(--wr-border)]">Purpose</th>
            </tr>
          </thead>
          <tbody>
            <tr><td className="px-4 py-3 border-b border-[var(--wr-border)] font-mono-wr text-[13px]">wr_locale</td><td className="px-4 py-3 border-b border-[var(--wr-border)]">Your interface language (en or ro).</td></tr>
            <tr><td className="px-4 py-3 font-mono-wr text-[13px]">wr_temp_unit</td><td className="px-4 py-3">Your temperature unit preference (C or F).</td></tr>
          </tbody>
        </table>
      </>,
    },
    {
      heading: "Analytics",
      body: <>
        <P>We use PostHog product analytics to understand aggregate usage. PostHog sets a small number of first-party cookies to distinguish sessions. No personally-identifying data is linked to these sessions on our side.</P>
        <P>These cookies help us improve WowRomania, not advertise to you.</P>
      </>,
    },
    {
      heading: "No advertising, no third-party ads",
      body: <P>We do not run display ads. We do not use Google Ads, Meta Pixel, TikTok Pixel or similar retargeting tools. If this ever changes, we will update this page and request consent whenever required by applicable law.</P>,
    },
    {
      heading: "Managing preferences",
      body: <P>You can clear all localStorage and cookies from WowRomania at any time via your browser's site-data controls. Doing so simply resets your interface language and temperature-unit preference to defaults.</P>,
    },
    {
      heading: "Contact",
      body: <P>Questions about cookies? Write to <a href="mailto:contact.wowromania@gmail.com" className="underline decoration-[var(--wr-red)] underline-offset-4">contact.wowromania@gmail.com</a>.</P>,
    },
  ] : [
    {
      heading: "Pe scurt",
      body: <>
        <P>WowRomania nu folosește cookie-uri de publicitate. Nu folosim tracking între site-uri. Mai jos explicăm cele câteva cookie-uri tehnice și date de browser pe care le folosim.</P>
        <P>Credem că ar trebui să știi exact ce este stocat în browserul tău și de ce.</P>
      </>,
    },
    {
      heading: "localStorage (doar în browserul tău)",
      body: <>
        <P>Două preferințe mici sunt stocate în localStorage-ul browserului tău. Nu părăsesc niciodată dispozitivul tău și nu le vedem niciodată:</P>
        <table className="w-full text-[14px] mt-2 border border-[var(--wr-border)]">
          <thead className="bg-[var(--wr-surface-2)]">
            <tr>
              <th className="text-left font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)] px-4 py-3 border-b border-[var(--wr-border)]">Cheie</th>
              <th className="text-left font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)] px-4 py-3 border-b border-[var(--wr-border)]">Scop</th>
            </tr>
          </thead>
          <tbody>
            <tr><td className="px-4 py-3 border-b border-[var(--wr-border)] font-mono-wr text-[13px]">wr_locale</td><td className="px-4 py-3 border-b border-[var(--wr-border)]">Limba interfeței (en sau ro).</td></tr>
            <tr><td className="px-4 py-3 font-mono-wr text-[13px]">wr_temp_unit</td><td className="px-4 py-3">Unitatea de temperatură preferată (C sau F).</td></tr>
          </tbody>
        </table>
      </>,
    },
    {
      heading: "Analytics",
      body: <>
        <P>Folosim PostHog pentru a înțelege utilizarea agregată. PostHog setează câteva cookie-uri first-party pentru a distinge sesiuni. Datele care ar putea identifica o persoană nu sunt legate de aceste sesiuni de partea noastră.</P>
        <P>Aceste cookie-uri ne ajută să îmbunătățim WowRomania, nu să îți afișăm reclame.</P>
      </>,
    },
    {
      heading: "Fără publicitate, fără reclame terțe",
      body: <P>Nu difuzăm reclame display. Nu folosim Google Ads, Meta Pixel, TikTok Pixel sau tool-uri similare de retargeting. Dacă acest lucru se va schimba, vom actualiza această pagină și vom solicita consimțământul tău ori de câte ori legea aplicabilă o va impune.</P>,
    },
    {
      heading: "Cum îți gestionezi preferințele",
      body: <P>Poți șterge oricând tot localStorage-ul și cookie-urile WowRomania din setările de date de site ale browserului tău. Efectul e doar resetarea preferințelor de limbă și temperatură la valorile implicite.</P>,
    },
    {
      heading: "Contact",
      body: <P>Întrebări despre cookie-uri? Scrie la <a href="mailto:contact.wowromania@gmail.com" className="underline decoration-[var(--wr-red)] underline-offset-4">contact.wowromania@gmail.com</a>.</P>,
    },
  ];

  return (
    <LegalShell
      kicker={en ? "Trust · Cookies" : "Încredere · Cookie-uri"}
      title={en ? "Cookie Policy" : "Politica cookie-urilor"}
      updated={en ? "February 15, 2026" : "15 februarie 2026"}
      description={en
        ? "The small number of browser cookies and storage items WowRomania uses. No advertising cookies. No cross-site tracking."
        : "Cele câteva cookie-uri de browser și date de stocare pe care le folosește WowRomania. Fără cookie-uri de publicitate. Fără tracking între site-uri."}
      path="/cookies"
      sections={sections}
    />
  );
}
